import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AssignSubscriptionOptionsToProductOptions,
  BulkCreateSubscriptionOptionsResponse,
  BulkUpdateSubscriptionOptionsResponse,
  GetOneTimePurchasesStatusResponse,
  GetProductIdsForSubscriptionOptionOptions,
  GetProductIdsForSubscriptionOptionResponse,
  GetSubscriptionOptionsForProductOptions,
  GetSubscriptionOptionsForProductResponse,
  SubscriptionOption,
  UpdateSubscriptionOption,
  allowOneTimePurchases as universalAllowOneTimePurchases,
  assignSubscriptionOptionsToProduct as universalAssignSubscriptionOptionsToProduct,
  bulkCreateSubscriptionOptions as universalBulkCreateSubscriptionOptions,
  bulkDeleteSubscriptionOptions as universalBulkDeleteSubscriptionOptions,
  bulkUpdateSubscriptionOptions as universalBulkUpdateSubscriptionOptions,
  createSubscriptionOption as universalCreateSubscriptionOption,
  deleteSubscriptionOption as universalDeleteSubscriptionOption,
  getOneTimePurchasesStatus as universalGetOneTimePurchasesStatus,
  getProductIdsForSubscriptionOption as universalGetProductIdsForSubscriptionOption,
  getSubscriptionOption as universalGetSubscriptionOption,
  getSubscriptionOptionsForProduct as universalGetSubscriptionOptionsForProduct,
  updateSubscriptionOption as universalUpdateSubscriptionOption,
} from './stores-v1-subscription-option-subscription-options.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/stores' };

export function getSubscriptionOption(
  httpClient: HttpClient
): GetSubscriptionOptionSignature {
  return (_id: string) =>
    universalGetSubscriptionOption(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetSubscriptionOptionSignature {
  /**
   * Retrieves a subscription option by ID.
   * @param - Subscription option ID.
   * @returns Subscription option.
   * @deprecated
   */
  (_id: string): Promise<
    NonNullablePaths<
      SubscriptionOption,
      | `subscriptionSettings.frequency`
      | `subscriptionSettings.autoRenewal`
      | `discount.type`
      | `discount.value`,
      3
    >
  >;
}

export function getSubscriptionOptionsForProduct(
  httpClient: HttpClient
): GetSubscriptionOptionsForProductSignature {
  return (
    productId: string,
    options?: GetSubscriptionOptionsForProductOptions
  ) =>
    universalGetSubscriptionOptionsForProduct(
      productId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetSubscriptionOptionsForProductSignature {
  /**
   * Retrieves all subscription options assigned to a specified product.
   * By default, hidden subscription options are not returned. To retrieve all subscription options you must pass `includeHiddenSubscriptionOptions = true`.
   * @param - Product ID.
   * @param - Options.
   * @deprecated
   */
  (
    productId: string,
    options?: GetSubscriptionOptionsForProductOptions
  ): Promise<
    NonNullablePaths<
      GetSubscriptionOptionsForProductResponse,
      | `subscriptionOptions`
      | `subscriptionOptions.${number}._id`
      | `subscriptionOptions.${number}.hidden`
      | `subscriptionOptions.${number}.subscriptionSettings.frequency`
      | `subscriptionOptions.${number}.subscriptionSettings.autoRenewal`
      | `subscriptionOptions.${number}.discount.type`
      | `subscriptionOptions.${number}.discount.value`,
      5
    >
  >;
}

export function getProductIdsForSubscriptionOption(
  httpClient: HttpClient
): GetProductIdsForSubscriptionOptionSignature {
  return (_id: string, options?: GetProductIdsForSubscriptionOptionOptions) =>
    universalGetProductIdsForSubscriptionOption(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetProductIdsForSubscriptionOptionSignature {
  /**
   * Retrieves the IDs of products associated with a specified subscription option.
   * @param - Subscription option ID.
   * @param - Paging and other options.
   * @deprecated
   */
  (_id: string, options?: GetProductIdsForSubscriptionOptionOptions): Promise<
    NonNullablePaths<
      GetProductIdsForSubscriptionOptionResponse,
      `productIds` | `metadata.items` | `metadata.offset` | `totalResults`,
      3
    >
  >;
}

export function getOneTimePurchasesStatus(
  httpClient: HttpClient
): GetOneTimePurchasesStatusSignature {
  return (productId: string) =>
    universalGetOneTimePurchasesStatus(
      productId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetOneTimePurchasesStatusSignature {
  /**
   * Checks whether a specified product (associated with subscription options) is available for one-time purchase.
   * @param - Product ID.
   * @deprecated
   */
  (productId: string): Promise<
    NonNullablePaths<GetOneTimePurchasesStatusResponse, `allowed`, 2>
  >;
}

export function createSubscriptionOption(
  httpClient: HttpClient
): CreateSubscriptionOptionSignature {
  return (
    subscriptionOption: NonNullablePaths<
      SubscriptionOption,
      `subscriptionSettings` | `title`,
      2
    >
  ) =>
    universalCreateSubscriptionOption(
      subscriptionOption,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateSubscriptionOptionSignature {
  /**
   * Creates a subscription option.
   * To assign to a product, call [`assignSubscriptionOptionsToProduct()`](https://www.wix.com/velo/reference/wix-stores-v2/subscriptionoptions/assign-subscription-options-to-product).
   * Subscription options that are not assigned to a product will not be visible in the Wix business manager.
   * @param - Subscription option info.
   * @returns Newly created subscription option.
   * @deprecated
   */
  (
    subscriptionOption: NonNullablePaths<
      SubscriptionOption,
      `subscriptionSettings` | `title`,
      2
    >
  ): Promise<
    NonNullablePaths<
      SubscriptionOption,
      | `subscriptionSettings.frequency`
      | `subscriptionSettings.autoRenewal`
      | `discount.type`
      | `discount.value`,
      3
    >
  >;
}

export function updateSubscriptionOption(
  httpClient: HttpClient
): UpdateSubscriptionOptionSignature {
  return (_id: string, subscriptionOption: UpdateSubscriptionOption) =>
    universalUpdateSubscriptionOption(
      _id,
      subscriptionOption,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateSubscriptionOptionSignature {
  /**
   * Updates a subscription option.
   * Only parameters passed will be updated.
   * @param - Subscription option ID (auto-generated upon subscription option creation).
   * @param - Subscription option update options.
   * @returns Updated subscription option.
   * @deprecated
   */
  (_id: string, subscriptionOption: UpdateSubscriptionOption): Promise<
    NonNullablePaths<
      SubscriptionOption,
      | `subscriptionSettings.frequency`
      | `subscriptionSettings.autoRenewal`
      | `discount.type`
      | `discount.value`,
      3
    >
  >;
}

export function deleteSubscriptionOption(
  httpClient: HttpClient
): DeleteSubscriptionOptionSignature {
  return (_id: string) =>
    universalDeleteSubscriptionOption(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteSubscriptionOptionSignature {
  /**
   * Deletes a subscription option.
   * @param - ID of the subscription option to delete.
   * @deprecated
   */
  (_id: string): Promise<void>;
}

export function bulkCreateSubscriptionOptions(
  httpClient: HttpClient
): BulkCreateSubscriptionOptionsSignature {
  return (
    subscriptionOptions: NonNullablePaths<
      SubscriptionOption,
      `subscriptionSettings` | `title`,
      2
    >[]
  ) =>
    universalBulkCreateSubscriptionOptions(
      subscriptionOptions,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateSubscriptionOptionsSignature {
  /**
   * Creates multiple subscription options (up to 100).
   * To assign to a product, call [`assignSubscriptionOptionsToProduct()`](https://www.wix.com/velo/reference/wix-stores-v2/subscriptionoptions/assign-subscription-options-to-product).
   * Subscription options that are not assigned to a product will not be visible in the Wix business manager.
   * @param - Subscription options info.
   * @deprecated
   */
  (
    subscriptionOptions: NonNullablePaths<
      SubscriptionOption,
      `subscriptionSettings` | `title`,
      2
    >[]
  ): Promise<
    NonNullablePaths<
      BulkCreateSubscriptionOptionsResponse,
      | `subscriptionOptions`
      | `subscriptionOptions.${number}.subscriptionSettings.frequency`
      | `subscriptionOptions.${number}.subscriptionSettings.autoRenewal`
      | `subscriptionOptions.${number}.discount.type`
      | `subscriptionOptions.${number}.discount.value`,
      5
    >
  >;
}

export function bulkUpdateSubscriptionOptions(
  httpClient: HttpClient
): BulkUpdateSubscriptionOptionsSignature {
  return (
    subscriptionOptions: NonNullablePaths<SubscriptionOption, `_id`, 2>[]
  ) =>
    universalBulkUpdateSubscriptionOptions(
      subscriptionOptions,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateSubscriptionOptionsSignature {
  /**
   * Updates multiple subscription options.
   * Only parameters passed will be updated.
   * @param - Subscription options info. Only the passed parameters in each subscription option will be updated.
   * @deprecated
   */
  (
    subscriptionOptions: NonNullablePaths<SubscriptionOption, `_id`, 2>[]
  ): Promise<
    NonNullablePaths<
      BulkUpdateSubscriptionOptionsResponse,
      | `subscriptionOptions`
      | `subscriptionOptions.${number}.subscriptionSettings.frequency`
      | `subscriptionOptions.${number}.subscriptionSettings.autoRenewal`
      | `subscriptionOptions.${number}.discount.type`
      | `subscriptionOptions.${number}.discount.value`,
      5
    >
  >;
}

export function bulkDeleteSubscriptionOptions(
  httpClient: HttpClient
): BulkDeleteSubscriptionOptionsSignature {
  return (ids: string[]) =>
    universalBulkDeleteSubscriptionOptions(
      ids,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteSubscriptionOptionsSignature {
  /**
   * Deletes multiple subscription options.
   * @param - IDs of the subscription options to delete.
   * @deprecated
   */
  (ids: string[]): Promise<void>;
}

export function assignSubscriptionOptionsToProduct(
  httpClient: HttpClient
): AssignSubscriptionOptionsToProductSignature {
  return (
    productId: string,
    options?: AssignSubscriptionOptionsToProductOptions
  ) =>
    universalAssignSubscriptionOptionsToProduct(
      productId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AssignSubscriptionOptionsToProductSignature {
  /**
   * Assign up to 6 subscription options to a specified product.
   * Pass an empty array to remove all subscription options assigned to a product.
   * @param - Product ID.
   * @param - Subscription option assignment options.
   * @deprecated
   */
  (
    productId: string,
    options?: AssignSubscriptionOptionsToProductOptions
  ): Promise<void>;
}

export function allowOneTimePurchases(
  httpClient: HttpClient
): AllowOneTimePurchasesSignature {
  return (productId: string, allowed: boolean) =>
    universalAllowOneTimePurchases(
      productId,
      allowed,
      // @ts-ignore
      { httpClient }
    );
}

interface AllowOneTimePurchasesSignature {
  /**
   * Allow for one-time purchase of a product.
   * By default, product can be sold only as part of a subscription, not as a one-time purchase.
   * @param - Product ID.
   * @param - Pass `true` to offer product by subscription and as one-time purchase. Pass `false` to offer product as subscription only.
   * @deprecated
   */
  (productId: string, allowed: boolean): Promise<void>;
}

export {
  AllowOneTimePurchasesRequest,
  AllowOneTimePurchasesResponse,
  AssignSubscriptionOptionsToProductOptions,
  AssignSubscriptionOptionsToProductRequest,
  AssignSubscriptionOptionsToProductResponse,
  BulkCalculatePricesRequest,
  BulkCalculatePricesRequestItem,
  BulkCalculatePricesRequestV2,
  BulkCalculatePricesResponse,
  BulkCalculatePricesResponseItem,
  BulkCalculatePricesResponseV2,
  BulkCreateSubscriptionOptionsRequest,
  BulkCreateSubscriptionOptionsResponse,
  BulkDeleteSubscriptionOptionsRequest,
  BulkDeleteSubscriptionOptionsResponse,
  BulkUpdateSubscriptionOptionsRequest,
  BulkUpdateSubscriptionOptionsResponse,
  CalculatePricesRequest,
  CalculatePricesResponse,
  CreateSubscriptionOptionRequest,
  CreateSubscriptionOptionResponse,
  DeleteSubscriptionOptionRequest,
  DeleteSubscriptionOptionResponse,
  Discount,
  DiscountType,
  GetOneTimePurchasesStatusRequest,
  GetOneTimePurchasesStatusResponse,
  GetProductIdsForSubscriptionOptionOptions,
  GetProductIdsForSubscriptionOptionRequest,
  GetProductIdsForSubscriptionOptionResponse,
  GetSubscriptionOptionRequest,
  GetSubscriptionOptionResponse,
  GetSubscriptionOptionsForProductOptions,
  GetSubscriptionOptionsForProductRequest,
  GetSubscriptionOptionsForProductResponse,
  ListSubscriptionOptionsRequest,
  ListSubscriptionOptionsResponse,
  Paging,
  PagingMetadata,
  SubscriptionFrequency,
  SubscriptionOption,
  SubscriptionOptionInProduct,
  SubscriptionOptionPriceData,
  SubscriptionOptionPrices,
  SubscriptionSettings,
  UpdateSubscriptionOption,
  UpdateSubscriptionOptionRequest,
  UpdateSubscriptionOptionResponse,
} from './stores-v1-subscription-option-subscription-options.universal.js';
